/* eslint-disable prettier/prettier */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import TextField from '@material-ui/core/TextField';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { Button, FormControlLabel, Switch } from '@mui/material';
import { StepContainer, StepTitle, FormGroup } from './styles';

const DEFAULT_BATCH_MESSAGE = `Olá, {nomeCliente}.

Pedimos desculpas pelo transtorno. 😔

Identificamos um problema no sistema de recorrência relacionado à sua assinatura da atividade {atividade}. Por esse motivo, será necessário refazer o pagamento da assinatura.


Você pode realizar o pagamento por aqui: {linkPayCliente}

Após essa regularização, a cobrança da assinatura seguirá normalmente em recorrência.


Em caso de dúvidas, entre em contato pelo telefone 3214-7300, opção 2. 📞

Abraço,
Equipe Sindilegis 👋`;

const AVAILABLE_MESSAGE_VARIABLES = [
  '{nomeCliente}',
  '{atividade}',
  '{linkPayCliente}',
  '{valor}',
  '{validade}',
];

const renderTextField = ({ input, label, meta: { touched, error }, ...custom }) => {
  console.log('Field:', input.name, 'Value:', input.value);
  return (
    <TextField
      label={label}
      variant="outlined"
      error={touched && Boolean(error)}
      helperText={touched && error}
      name={input.name}
      value={input.value || ''}
      onChange={(e) => {
        console.log('onChange:', input.name, 'New value:', e.target.value);
        input.onChange(e.target.value);
      }}
      onBlur={(e) => input.onBlur(e.target.value)}
      onFocus={input.onFocus}
      {...custom}
      fullWidth
    />
  );
};

const normalizeHeader = (value) => {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
};

const getMappedValue = (row, aliases) => {
  const normalizedRow = Object.entries(row || {}).reduce((acc, [key, value]) => {
    acc[normalizeHeader(key)] = value;
    return acc;
  }, {});

  const match = aliases.find((alias) => normalizedRow[normalizeHeader(alias)] !== undefined);
  return match ? String(normalizedRow[normalizeHeader(match)] || '').trim() : '';
};

const parseBatchCustomers = (rows) => {
  const customers = [];
  const errors = [];

  rows.forEach((row, index) => {
    const customer = {
      customer_name: getMappedValue(row, ['nome', 'cliente', 'nomedocliente', 'customer_name']),
      customer_cpf: getMappedValue(row, ['cpf', 'cpfdocliente', 'customer_cpf']),
      customer_email: getMappedValue(row, ['email', 'emaildocliente', 'customer_email']),
      customer_phone: getMappedValue(row, [
        'telefone',
        'celular',
        'whatsapp',
        'telefonedocliente',
        'customer_phone',
      ]),
      service_name: getMappedValue(row, [
        'atividade',
        'atividadeouservico',
        'servico',
        'nomedaatividade',
        'service_name',
      ]),
    };

    const rowErrors = [];
    if (!customer.customer_name) rowErrors.push('Nome do cliente não informado');
    if (!customer.customer_cpf) rowErrors.push('CPF do cliente não informado');
    if (!customer.service_name) rowErrors.push('Atividade/serviço não informado');

    if (rowErrors.length > 0) {
      errors.push({
        row: index + 2,
        message: rowErrors.join(' | '),
      });
      return;
    }

    customers.push(customer);
  });

  return { customers, errors };
};

const downloadBatchTemplate = (fileType) => {
  const worksheet = XLSX.utils.aoa_to_sheet([
    ['nome', 'cpf', 'email', 'telefone', 'atividade'],
    ['', '', '', '', ''],
  ]);
  const workbook = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(workbook, worksheet, 'Clientes');

  if (fileType === 'csv') {
    XLSX.writeFile(workbook, 'modelo-link-pagamento-em-massa.csv', {
      bookType: 'csv',
    });
    return;
  }

  XLSX.writeFile(workbook, 'modelo-link-pagamento-em-massa.xlsx', {
    bookType: 'xlsx',
  });
};

const parseCsvFile = (file) => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => resolve(results.data || []),
      error: (error) => reject(error),
    });
  });
};

const parseSpreadsheetFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (loadEvent) => {
      try {
        const binaryContent = loadEvent.target?.result;
        const workbook = XLSX.read(binaryContent, { type: 'binary' });
        const firstSheetName = workbook.SheetNames[0];
        const firstSheet = workbook.Sheets[firstSheetName];
        const rows = XLSX.utils.sheet_to_json(firstSheet, { defval: '' });

        resolve(rows);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = (error) => reject(error);
    reader.readAsBinaryString(file);
  });
};

const DadosPessoais = ({ formValues, change }) => {
  useEffect(() => {
    if (formValues?.batch_mode === undefined) {
      change('batch_mode', false);
    }

    if (formValues?.batch_mode && formValues?.custom_message === undefined) {
      change('custom_message', DEFAULT_BATCH_MESSAGE);
    }
  }, [change, formValues?.batch_mode, formValues?.custom_message]);

  const handleBatchModeChange = (event) => {
    const checked = event.target.checked;
    change('batch_mode', checked);

    if (!checked) {
      change('batch_customers', []);
      change('batch_import_errors', []);
      change('batch_file_name', '');
      change('custom_message', '');
      return;
    }

    change('service_name', '');

    if (!formValues?.custom_message) {
      change('custom_message', DEFAULT_BATCH_MESSAGE);
    }
  };

  const handleFileImport = async (event) => {
    const inputElement = event.target;
    const file = inputElement?.files?.[0];

    if (!file) {
      return;
    }

    try {
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      let rows = [];

      if (fileExtension === 'csv') {
        rows = await parseCsvFile(file);
      } else {
        rows = await parseSpreadsheetFile(file);
      }

      const { customers, errors } = parseBatchCustomers(rows);

      change('batch_file_name', file.name);
      change('batch_customers', customers);
      change('batch_import_errors', errors);
    } catch (error) {
      change('batch_file_name', file.name);
      change('batch_customers', []);
      change('batch_import_errors', [
        {
          row: '-',
          message: 'Não foi possível processar o arquivo informado.',
        },
      ]);
    } finally {
      if (inputElement) {
        inputElement.value = '';
      }
    }
  };

  const importedCustomers = formValues?.batch_customers || [];
  const importedErrors = formValues?.batch_import_errors || [];

  const handleRemoveImportedCustomer = (customerIndex) => {
    change(
      'batch_customers',
      importedCustomers.filter((_, index) => index !== customerIndex)
    );
  };

  return (
    <StepContainer>
      <StepTitle>Dados do Cliente</StepTitle>

      <FormGroup>
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(formValues?.batch_mode)}
              onChange={handleBatchModeChange}
              color="primary"
            />
          }
          label="Gerar links em massa por arquivo CSV/XLSX"
        />
      </FormGroup>

      {!formValues?.batch_mode && (
        <FormGroup>
          <Field
            name="service_name"
            component={renderTextField}
            label="Atividade/Serviço *"
            placeholder="Digite um nome para o link de pagamento"
            fullWidth
            required
          />
        </FormGroup>
      )}

      {!formValues?.batch_mode && (
        <>
          <FormGroup>
            <Field
              name="customer_name"
              component={renderTextField}
              label="Nome do cliente *"
              placeholder="Digite o nome do cliente que irá pagar"
              fullWidth
              required
            />
          </FormGroup>

          <FormGroup>
            <Field
              name="customer_cpf"
              component={renderTextField}
              label="CPF do cliente *"
              placeholder="___.___.___-__"
              fullWidth
              required
              normalize={(value) => {
                if (!value) return value;
                const onlyNums = value.replace(/[^\d]/g, '');
                if (onlyNums.length <= 3) return onlyNums;
                if (onlyNums.length <= 6) return `${onlyNums.slice(0, 3)}.${onlyNums.slice(3)}`;
                if (onlyNums.length <= 9) {
                  return `${onlyNums.slice(0, 3)}.${onlyNums.slice(3, 6)}.${onlyNums.slice(6)}`;
                }
                return `${onlyNums.slice(0, 3)}.${onlyNums.slice(3, 6)}.${onlyNums.slice(6, 9)}-${onlyNums.slice(9, 11)}`;
              }}
            />
          </FormGroup>

          <FormGroup>
            <Field
              name="customer_email"
              component={renderTextField}
              label="E-mail do cliente"
              placeholder="Digite o e-mail do cliente"
              type="email"
              fullWidth
            />
          </FormGroup>

          <FormGroup>
            <Field
              name="customer_phone"
              component={renderTextField}
              label="Telefone do cliente"
              placeholder="(__) _____-____"
              fullWidth
              normalize={(value) => {
                if (!value) return value;
                const onlyNums = value.replace(/[^\d]/g, '');
                if (onlyNums.length <= 2) return `(${onlyNums}`;
                if (onlyNums.length <= 7) return `(${onlyNums.slice(0, 2)}) ${onlyNums.slice(2)}`;
                return `(${onlyNums.slice(0, 2)}) ${onlyNums.slice(2, 7)}-${onlyNums.slice(7, 11)}`;
              }}
            />
          </FormGroup>
        </>
      )}

      {formValues?.batch_mode && (
        <>
          <FormGroup>
            <div
              style={{
                padding: '12px 16px',
                backgroundColor: '#e3f2fd',
                borderLeft: '4px solid #2196f3',
                borderRadius: '4px',
                fontSize: '14px',
                color: '#1976d2',
              }}
            >
              O arquivo deve conter as colunas: <strong>nome</strong>, <strong>cpf</strong>, <strong>email</strong>, <strong>telefone</strong> e <strong>atividade</strong>.
            </div>
          </FormGroup>

          <FormGroup>
            <Button variant="outlined" component="label">
              Importar arquivo CSV/XLSX
              <input hidden type="file" accept=".csv,.xlsx,.xls" onChange={handleFileImport} />
            </Button>
            <div style={{ display: 'flex', gap: '12px', marginTop: '12px', flexWrap: 'wrap' }}>
              <Button variant="text" color="primary" onClick={() => downloadBatchTemplate('csv')}>
                Baixar modelo CSV
              </Button>
              <Button variant="text" color="primary" onClick={() => downloadBatchTemplate('xlsx')}>
                Baixar modelo XLSX
              </Button>
            </div>
            {formValues?.batch_file_name && (
              <div style={{ marginTop: '8px', fontSize: '13px', color: '#555' }}>
                Arquivo: {formValues.batch_file_name}
              </div>
            )}
          </FormGroup>

          <FormGroup>
            <div style={{ fontSize: '14px', color: '#333' }}>
              Clientes válidos importados: <strong>{importedCustomers.length}</strong>
            </div>
            {importedErrors.length > 0 && (
              <div style={{ marginTop: '8px', fontSize: '13px', color: '#d32f2f' }}>
                Linhas com erro: <strong>{importedErrors.length}</strong>
              </div>
            )}
          </FormGroup>

          {importedCustomers.length > 0 && (
            <FormGroup>
              <div
                style={{
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  maxHeight: '220px',
                  overflow: 'auto',
                }}
              >
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Nome</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>CPF</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>E-mail</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Telefone</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Atividade</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importedCustomers.map((customer, index) => (
                      <tr key={`${customer.customer_cpf}-${index}`}>
                        <td style={{ padding: '10px', borderBottom: '1px solid #f0f0f0' }}>{customer.customer_name}</td>
                        <td style={{ padding: '10px', borderBottom: '1px solid #f0f0f0' }}>{customer.customer_cpf}</td>
                        <td style={{ padding: '10px', borderBottom: '1px solid #f0f0f0' }}>{customer.customer_email || '-'}</td>
                        <td style={{ padding: '10px', borderBottom: '1px solid #f0f0f0' }}>{customer.customer_phone || '-'}</td>
                        <td style={{ padding: '10px', borderBottom: '1px solid #f0f0f0' }}>{customer.service_name || formValues?.service_name || '-'}</td>
                        <td style={{ padding: '10px', borderBottom: '1px solid #f0f0f0' }}>
                          <Button
                            variant="text"
                            color="error"
                            size="small"
                            onClick={() => handleRemoveImportedCustomer(index)}
                          >
                            Remover
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </FormGroup>
          )}

          {importedErrors.length > 0 && (
            <FormGroup>
              <div
                style={{
                  padding: '12px 16px',
                  backgroundColor: '#ffebee',
                  borderLeft: '4px solid #f44336',
                  borderRadius: '4px',
                  fontSize: '13px',
                  color: '#c62828',
                }}
              >
                {importedErrors.slice(0, 5).map((item, index) => (
                  <div key={`${item.row}-${index}`}>Linha {item.row}: {item.message}</div>
                ))}
              </div>
            </FormGroup>
          )}
        </>
      )}

      {formValues?.batch_mode && (
        <>
          <FormGroup>
            <Field
              name="custom_message"
              component={renderTextField}
              label="Mensagem personalizada"
              placeholder="Use {nomeCliente}, {atividade}, {linkPayCliente}, {valor} e {validade}"
              fullWidth
              multiline
              minRows={12}
            />
          </FormGroup>

          <FormGroup>
            <div
              style={{
                padding: '12px 16px',
                backgroundColor: '#fff8e1',
                borderLeft: '4px solid #ffb300',
                borderRadius: '4px',
                fontSize: '13px',
                color: '#8d6e63',
              }}
            >
              Variáveis disponíveis: <strong>{AVAILABLE_MESSAGE_VARIABLES.join(', ')}</strong>
            </div>
          </FormGroup>

          <FormGroup>
            <div
              style={{
                padding: '12px 16px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                fontSize: '13px',
                color: '#555',
              }}
            >
              Os links serão gerados e enviados automaticamente por e-mail e WhatsApp na etapa de confirmação, quando essas informações estiverem preenchidas no arquivo.
            </div>
          </FormGroup>
        </>
      )}
    </StepContainer>
  );
};

DadosPessoais.propTypes = {
  change: PropTypes.func.isRequired,
  formValues: PropTypes.object,
};

export default DadosPessoais;
